import React, { PureComponent } from 'react';
import { noSelect } from '../constants/Styles';

import sleep from 'sleep-promise';

import { getLockProxyHost, setLockProxyHost, getAudioPrimed, getMbotConfig, getUseFirestore } from '../constants/Meta';
import {  allowMulti, randomHex, getShared, decodeUrlParams, arrLast } from '../vodjs/Utils';
import Header from './DRHeader';

import {
  configureUnit, registerRelog, getRelog, clearRelog, 
  stashConfig, r2h, h2r, robotCodeToFriendly, robotFriendlyToCode, waitForCodeMaps
} from '../vodjs/lib.js';
import { login, isFSLoggedIn } from '../vodjs/settings';
import VersionDisplay from './VersionDisplay';

import { elasticLogConfig, elasticLogEvent, elasticLogError, getMachineUid, getUnitConfig } from '../vodjs/ElasticLog';
import { startErrorHandling } from '../vodjs/ErrorHandling';
import { isMobile, isWideMobile, clearFetch } from '../vodjs/Utils';
import { PollMgrStartPolling, PollMgrStopPolling } from '../vodjs/PollMgr';
import { getConfig, setConfig } from '../utils/configUtils';
import { reloadNoCache } from '../vodjs/Network';

const mobile = isMobile();
const gripperIconSz = mobile ? 24 : 45;

const NO_ROBOT = 'None';

const lockType = 'storage_plus';
const lockNames = ['small', 'medium', 'large'];

const UNVERIFIED_TASK_EXEC = "Note: cannot verify that robot's task-executor is running.";

const session = randomHex(8);  //used for doppelganger detection; in-mem only
getShared().hallpassSession = session;

const defaultStrideSecs = 60 * 5;
let pingLooping = false, postCheckGoose = false, checkLoopTimeout, loggedinCB;

let urlParams = {};
let mostRecentSpurTicks;

let msgSetter = () => {};

export default class HallpassLogin extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      error: false,
      userLoggedIn: false,
      username: '',
      password: '',
      unit: '',
      robot: '',
      units: null,
      robots: null,
      userTypes: null,
      unitSubmitted: false,
      isLoading: false,
      showSuccess: false,
      configurationType: undefined,
      tubeProxyVerified: false,
    };
    startErrorHandling();

    this.handleDimensionChange = this.handleDimensionChange.bind(this);
    this.state.wideMobile = isWideMobile();
    this.usernameRef = React.createRef();

    let relogData = getRelog(); //if truthy: {data, userAck}
    this.userAck = !!(relogData || {}).userAck;
    this.priorSession = (relogData || {}).data;
    this.nextRelog = this.priorSession || {};

    this.tappableRef = React.createRef();
  }

  handleDimensionChange(event) {
    this.setState({ wideMobile: isWideMobile() });
  }

  //this is to pre-fetch resources that the robot will need when the internet might be bad
  async preloadImages() {
    let preload = [
      require('../assets/images/close-x.png'),
      require('../assets/images/x.png'),
      require('../assets/images/splus_moxi_front_back-closed.png'),
      require('../assets/images/questionMarkInCircle.png'),
      require('../assets/images/blue_gripper.png'),
      require('../assets/images/icon-airdrop.png'),
      require('../assets/images/close_no_fill.png'),
      require('../assets/images/tick_no_fill.png'),
      require('../assets/images/left-select.png'),
      require('../assets/images/right-select.png'),
      require('../assets/images/splus/large/requested.png'),
      require('../assets/images/splus/large/unlocked.png'),
      require('../assets/images/splus/large/filled.png'),
      require('../assets/images/splus/large/locked.png'),
      require('../assets/images/splus/medium/requested.png'),
      require('../assets/images/splus/medium/unlocked.png'),
      require('../assets/images/splus/medium/filled.png'),
      require('../assets/images/splus/medium/locked.png'),
      require('../assets/images/splus/small/requested.png'),
      require('../assets/images/splus/small/unlocked.png'),
      require('../assets/images/splus/small/filled.png'),
      require('../assets/images/splus/small/locked.png'),
      require('../assets/images/welcome_diligent_logo.png'),
      require('../assets/images/moxi-badge-reader.gif'),
      require('../assets/images/moxi-question.png'),
      require('../assets/images/moxi-at-door.png'),
      require('../assets/images/estop-button.png'),
    ];

    let images = [];

    for (var i = 0; i < preload.length; i++) {
      let img = new Image();
      img.src = preload[i];
      images.push(img);
    }

    window.preloadedImages = images;
  }

  //TODO remove display of url in tapped version info

  componentDidMount() {
    window.addEventListener('resize', this.handleDimensionChange);
    this.preloadImages(); //async
    this.hallpassLogin(); //async
  }


  handleErr(what, backendMsg, userMsg) {
    elasticLogError({ what, msg: backendMsg });

    userMsg = userMsg || `The system encountered issues; contact Diligent`;
    this.setState({ hallpass: { userMsg } });

    return { errInfo: { what, msg: backendMsg } };
  }


  async loginAndConfig(json) {
    let phase;
    try {
      let { actionCode, userMsg, configId, fsCreds, elastic } = json;

      //attempt firestore login
      phase = 'fsLogin';
      this.setState({ hallpass: { userMsg: fsCreds.noFS ? `Connecting to server subscriptions...` : `Connecting to firestore...` } });
      await login(fsCreds.name, fsCreds.password, fsCreds.noFS);

      setMbotElasticCreds(elastic);

      phase = 'codeMapsWait';
      this.setState({ hallpass: { userMsg: `Loading codeMaps...` } });
      await waitForCodeMaps();

      let stationConfig, robotConfig, cfgUserMsg = userMsg;
      if (!configId) {
        return this.handleErr('hpNoConfig', 'No station or robot was specified');
      }
      else if (configId.startsWith('moxi')) {
        //it's a robot
        cfgUserMsg = cfgUserMsg || `Configuring as ${robotCodeToFriendly(configId) || configId}...`;
        robotConfig = { id: configId };
      }
      else {
        //it's a station
        cfgUserMsg = cfgUserMsg || `Configuring as ${r2h(configId) || configId}...`;
        stationConfig = { id: configId };
      }

      //configure
      phase = 'configure';
      this.setState({ hallpass: { userMsg: cfgUserMsg } });
      await configureUnit(stationConfig, robotConfig, lockType, lockNames);

      //post-configure
      phase = 'post-configure';

      postConfigure(robotConfig, stationConfig);

      this.setState({ isLoading: false, userLoggedIn: true });
      let useFirestore = getUseFirestore();
      elasticLogEvent({ what: 'loggedIn', msg: `version=${getConfig().version.branch} useFirestore=${useFirestore}`, useFirestore });

      return {}; //no error
    }
    catch (err) {
      return this.handleErr('loginExc', `Error in ${phase} phase: ` + err);
    }
  }


  async handleJsonAction(json, ctxState) {
    let done = false;

    setMbotElasticCreds(json.elastic);

    if (json.actionCode == 'doLogin') {
      let loginConfigResult = await this.loginAndConfig(json);
      if (!loginConfigResult.errInfo) {
        done = true; //exit loop
        this.props.setUserLoggedIn(); //leave this entire hallpass login phase, woohoo!
      }
      else {
        if(ctxState)
          ctxState.loopErrInfo = loginConfigResult.errInfo; //will be sent to back end on next attempt
      }
    }
    else if (json.actionCode == 'restartWait') {
      this.setState({
        hallpass: { userMsg: json.userMsg || 'This device is awaiting a hexnode restart...' },
      });
      if(ctxState)
        ctxState.waitingForRestart = true;
    }
    else if (json.actionCode == 'retry') {
      if(ctxState) {
        ctxState.waitingForRestart = false;
        ctxState.badResults = {};
      }
      this.setState({ hallpass: { userMsg: json.userMsg || 'Server issue, will retry...' } });
    }
    else if (json.actionCode == 'reload') {
      if(ctxState) {
        ctxState.waitingForRestart = false;
        ctxState.badResults = {};
      }
      this.setState({ hallpass: { userMsg: json.userMsg || 'Reloading...' } });
      elasticLogEvent({ what: 'hpReload', msg: `about to reload ${json.reloadUrl}` });
      let url = json.reloadUrl == 'default' ? undefined : json.reloadUrl;
      setTimeout(() => reloadNoCache(url), 1000 * 2); //escape time for logs
    }

    return done;
  }


  async hallpassLogin() {

    let siteMbot = getConfig().uiSimServer;
    if (!siteMbot) {
      this.setState({ hallpass: { error: `No mbot backend specified; contact Diligent.` } });
      return;
    }

    const loadUrl = getConfig().loadUrl;
    urlParams = decodeUrlParams(loadUrl);

    let mbotConfig = getMbotConfig();
    if(mbotConfig.useHallpass2) {
      loggedinCB = () => {
        this.setState({ isLoading: false, userLoggedIn: true });
        this.props.setUserLoggedIn();
        loggedinCB = undefined; //one-time operation
      };

      msgSetter = userMsg => this.setState({ hallpass: { userMsg } });
      startHallpassCheckLoop();
      return;
    }



    //reach mbot backend

    let options = {
      method: 'PUT',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    };
    const backendUrl = new URL('hallpassLogin', siteMbot);
    let done = false, json;
    this.setState({ hallpass: { userMsg: `Contacting server...` } });

    //info for the backend
    let ctxState = {
      waitingForRestart:false,    //whether this kiosk continues to wait for a restart
      loopErrInfo: undefined,
      badResults: {}
    };

    while (!done) {
      options.body = JSON.stringify({
        loadUrl,
        now: Date.now(),
        mid: getMachineUid(),
        ...ctxState
      });

      let waitSeconds;  //backend-supplied throttle

      await clearFetch(backendUrl, options, {
        success: async (jsonParam) => {
          ctxState.waitingForRestart = false;
          ctxState.badResults = {};
          ctxState.loopErrInfo = undefined; //backend has been informed of loopErrInfo, if any
          done = await this.handleJsonAction(jsonParam, ctxState);
          waitSeconds = jsonParam.waitSeconds;

          setMbotElasticCreds(jsonParam.elastic);
        },
        bad: ({ logStr }) => {
          if (Object.keys(ctxState.badResults).length < 10)
            //arbitrary limit
            ctxState.badResults[logStr] = (ctxState.badResults[logStr] || 0) + 1;

          if (ctxState.waitingForRestart) {
            //Even though we don't expressly need to reach the mbot backend (since we already did and are waiting for hexnode to
            // restart us), it's still worth surfacing to the user that we had a network error, because mbotSvc is supposed to
            // be up all the time, and if it's not, it's a canary for potential network issues on the ipad side or hospital side.
            this.setState({
              hallpass: { userMsg: `This device is awaiting a hexnode restart (network may need verifying)...` },
            });
          }
          else {
            this.setState({
              hallpass: { userMsg: `Unable to reach the server, which may indicate network issues; retrying...` },
            });
          }
        },
      });

      if (!done) {
        await sleep(1000 * (waitSeconds || (ctxState.waitingForRestart ? 60 : 10))); //arbitrary
      }
    }
  }

  componentWillUnmount() {
    this.unmounting = true;
    msgSetter = () => {};
    if (this.pingTimeout) {
      clearTimeout(this.pingTimeout);
      this.pingTimeout = undefined;
    }
    window.removeEventListener('resize', this.handleDimensionChange);
  }


  render() {

    return (
      <div
        style={{
          display: 'flex',
          flexFlow: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: `100vh`,
          minHeight: 200,
        }}
      >
        <Header isPreStation={true} />
        <div style={{ flex: 1 }} />

        <div style={{ fontSize: 24, textWrap: 'balance', textAlign: 'center', ...noSelect }}>
          {this.state.hallpass?.userMsg || 'Loading...'}
        </div>

        <div style={{ flex: 1 }} />

        <VersionDisplay />
      </div>
    );
  }
}


async function doCheck() {
  checkLoopTimeout = undefined; //so (pingLooping && !checkLoopTimeout) means loops is executing

  let config = getConfig();
  let siteMbot = config.uiSimServer;
  let { loadUrl, loadTicks } = config;
  let version = config.version?.branch || 'unknown';

  let localStrideSecs = Math.round(defaultStrideSecs * (0.95 + (0.1 * Math.random())));
  try {
    let unitConfig = getUnitConfig();
    let configId = '';
    if (unitConfig) {
      if (unitConfig.robot && unitConfig.robot != NO_ROBOT)
        configId = unitConfig.robot;
      else if (unitConfig.unit)
        configId = h2r(unitConfig.unit);
    }

    let dimParts = [];
    const dimAdd = widthHeightStr => dimParts.push(widthHeightStr == arrLast(dimParts) ? '*' : widthHeightStr);
    try {
      dimParts.push(`${window.innerWidth}x${window.innerHeight}`);
      dimAdd(`${document.documentElement.clientWidth}x${document.documentElement.clientHeight}`);
    }
    catch(err) {
      dimParts.push('' + err);
    }

    let screen;
    if(window.screen) {
      let {availHeight, availLeft, availTop, availWidth, colorDept, height, width, isExtended, pixelDepth} = window.screen; //orientation
      // let {angle, type} = orientation;  //seems to be abent sometimes?
      screen = {availHeight, availLeft, availTop, availWidth, colorDept, height, width, isExtended, pixelDepth};  //, orientation:{angle, type}
      dimAdd(`${availWidth}x${availHeight}`);
    }

    let interaction = getShared().interaction || 0;

    let clientData = {
      loadUrl,
      loadTicks,
      mid: getMachineUid(),
      version,
      configId, 
      fsLoggedIn:isFSLoggedIn(),
      audioPrimed:getAudioPrimed(),
      screen,
      session,
      interaction,
      dim: dimParts.join(' ')
    };

    let options = {
      method: 'PUT',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientData})
    };
    const backendUrl = new URL('hallpassActivePing', siteMbot);
    postCheckGoose = false;
    await clearFetch(backendUrl, options, async (json) => {

      setMbotElasticCreds(json.elastic);

      if (json.ping) {
        elasticLogEvent({ what: 'hpPing', msg: `ping ${json.ping}` });
      }

      if (json.reloadUrl) {
        elasticLogEvent({ what: 'hpReload', msg: `about to reload ${json.reloadUrl}` });
        let url = json.reloadUrl == 'default' ? undefined : json.reloadUrl;
        setTimeout(() => reloadNoCache(url), 1000 * 2); //escape time for logs
      }
      else if(json.configId) {
        //hot morph: change station without reloading: we get to keep our audio!

        let userMsg = json.userMsg; //can be undefined

        const setUserMsg = msg => msgSetter(json.userMsg ? `${json.userMsg} (${msg})` : msg);

        if(!isFSLoggedIn()) {
          if(!json.fsCreds) 
            throw new Error(`No fs connect context`);

          let fsCreds = json.fsCreds;
          let config = getConfig();
          setConfig({ ...config, mbotElastic: fsCreds.elastic || json.elastic }); //LMR-todo: remove fsCreds.elastic
          await login(fsCreds.name, fsCreds.password, fsCreds.noFS);
          await waitForCodeMaps();
        }

        let stationConfig, robotConfig;
        let configId = json.configId;
        if (configId.startsWith('moxi')) {
          //it's a robot
          // cfgUserMsg = cfgUserMsg || `Configuring as ${robotCodeToFriendly(configId) || configId}...`;
          robotConfig = { id: configId };
        }
        else {
          //it's a station
          // cfgUserMsg = cfgUserMsg || `Configuring as ${r2h(configId) || configId}...`;
          stationConfig = { id: configId };
        }
        elasticLogEvent({ what: 'hotMorph', msg: `hotmorphing to ${configId}`, configId });

        await configureUnit(stationConfig, robotConfig, lockType, lockNames);

        postConfigure(robotConfig, stationConfig);

        let useFirestore = getUseFirestore();
        elasticLogEvent({ what: 'loggedIn', msg: `version=${getConfig().version.branch} useFirestore=${useFirestore}`, useFirestore });

        if(loggedinCB)
          loggedinCB();

        localStrideSecs = Math.min(localStrideSecs, 1);  //quickly apprise the backend
      }
      else {
        if(json.elastic)
          setConfig({ ...getConfig(), mbotElastic: json.elastic });

        if(json.userMsg !== undefined)
          msgSetter(json.userMsg);
      }

    }, 5000);

    if(postCheckGoose) {
      localStrideSecs = Math.min(localStrideSecs, 1);  //check w backend again soon, need to do so came in while we were doing net call
      postCheckGoose = false;
    }
    else if(!isFSLoggedIn()) {
      //spur mechanism not yet available
      localStrideSecs = Math.min(localStrideSecs, 10 + Math.round(5 * Math.random()))
    }
  }
  catch (err) {
    if (allowMulti('hpLoopErr', 2, 15)) elasticLogError({ what: 'hpLoopErr', msg: '' + err });
  }
  finally {
    if (!pingLooping) {
      elasticLogEvent({ what: 'hpPingStop', msg: `Stopping hallpass pings` });
      return;
    }
    checkLoopTimeout = setTimeout(doCheck, Math.round(1000 * localStrideSecs));
  }
}



export function startHallpassCheckLoop() {
  if(pingLooping) return;
  pingLooping = true;
  checkLoopTimeout = setTimeout(doCheck, 1000);  //first loop iteration kickoff
}


export function stopHallpassCheckLoop() {
  pingLooping = false;
  if (checkLoopTimeout) {
    clearTimeout(checkLoopTimeout);
    checkLoopTimeout = undefined;
  }
}


export function hallpassHandleSpur(dvcMgmtTrigger) {
  if(!dvcMgmtTrigger?.spurs) return;
  let ticks = dvcMgmtTrigger.spurs[urlParams.deviceid];
  if(!ticks) return;
  if(mostRecentSpurTicks == ticks) return;

  mostRecentSpurTicks = ticks;

  if(!pingLooping) return;

  if(!checkLoopTimeout) {
    //loop is already executing
    postCheckGoose = true;
    return;
  }

  clearTimeout(checkLoopTimeout);
  checkLoopTimeout = setTimeout(doCheck, 1000);
}






function postConfigure(robotConfig, stationConfig) {

  //stash for compatibility
  let unit, unitCode, robot;
  if (robotConfig) {
    robot = robotConfig.friendly || robotCodeToFriendly(robotConfig.id);
  }
  else {
    unitCode = stationConfig.id || h2r(stationConfig.friendly);
    unit = stationConfig.friendly || r2h(stationConfig.id);
  }

  if (robotConfig) {
    let { firestore, lockProxyHost, lockProxyHostEncrypted } = getConfig();
    let lockProxyHostUsed = firestore.simServer ? lockProxyHost : lockProxyHostEncrypted;
    setLockProxyHost(lockProxyHostUsed);
    elasticLogEvent({ what: 'didSetLockProxy', msg: `set lockProxyHost to ${lockProxyHostUsed}` });

    PollMgrStartPolling();
  }
  else {
    PollMgrStopPolling();
    setLockProxyHost(undefined);
  }

  stashConfig({ unit, unitCode, robot, lockType, lockNames }); //used for legacy (pre-hp) pings on startup

  registerRelog({
    config: { unit, unitCode, robot, lockType, lockNames },
    lockProxyHost: getLockProxyHost()
  });
  clearRelog(); //unpersist

  const robotId = robotConfig ? robotConfig.id || robotFriendlyToCode(robotConfig.friendly) : NO_ROBOT;
  elasticLogConfig({ unit, robot:robotId, lockType, lockNames });
}



function setMbotElasticCreds(elastic) {
  if(!elastic) return;  //one-way latch
  let config = getConfig();
  setConfig({ ...config, mbotElastic: elastic });
}
